import type { RefObject } from 'react';
import { useCallback, useEffect, useEffectEvent, useState } from 'react';

export const useScrollPosition = (ref: RefObject<HTMLDivElement | null>) => {
  const [scrollPosition, setScrollPosition] = useState<
    'start' | 'middle' | 'end' | undefined
  >(undefined);

  const isScrollReverse = useCallback(
    (divRef: React.RefObject<HTMLDivElement | null>) => {
      if (divRef.current) {
        return getComputedStyle(divRef.current).flexDirection === 'row-reverse';
      }
      return false;
    },
    []
  );

  useEffect(() => {
    if (ref.current) {
      // oxlint-disable-next-line react/set-state-in-effect
      setScrollPosition(ref.current && isScrollReverse(ref) ? 'end' : 'start');
    }
  }, [isScrollReverse, ref]);

  const handleScroll = useEffectEvent(() => {
    if (ref.current) {
      const { offsetWidth, scrollLeft, scrollWidth } = ref.current;
      if (scrollLeft === 0) {
        const scrollPositionValue = isScrollReverse(ref) ? 'end' : 'start';
        if (scrollPosition !== scrollPositionValue) {
          setScrollPosition(scrollPositionValue);
        }
      } else if (
        /** +1 to take into account rounding differences */
        Math.round((offsetWidth || 0) + (scrollLeft || 0)) + 1 >=
        (scrollWidth || 0)
      ) {
        const scrollPositionValue = isScrollReverse(ref) ? 'start' : 'end';
        if (scrollPosition !== scrollPositionValue) {
          setScrollPosition(scrollPositionValue);
        }
      } else if (scrollPosition !== 'middle') {
        setScrollPosition('middle');
      }
    }
  });

  useEffect(() => {
    const divElement = ref.current;

    divElement?.addEventListener('scroll', handleScroll);
    return () => divElement?.removeEventListener('scroll', handleScroll);
  }, [ref]);

  return { scrollPosition };
};
